import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import {useId} from 'react';
import {useTranslation} from 'react-i18next';

type AiChart = {
  version: 1;
  type: 'line' | 'bar';
  title: string;
  xAxis: {label?: string; values: string[]};
  yAxis: {label?: string; unit?: string};
  series: Array<{name: string; values: Array<number | null>}>;
};

type AiChartBlockProps = {
  source: string;
};

export const parseAiChart = (source: string): AiChart | null => {
  try {
    if (new TextEncoder().encode(source).length > 20 * 1024) return null;
    const chart: unknown = JSON.parse(source);
    if (
      !isRecord(chart) ||
      !hasOnlyKeys(chart, ['version', 'type', 'title', 'xAxis', 'yAxis', 'series'])
    ) {
      return null;
    }
    if (
      chart.version !== 1 ||
      (chart.type !== 'line' && chart.type !== 'bar') ||
      !isRequiredString(chart.title, 120)
    ) {
      return null;
    }
    if (
      !isRecord(chart.xAxis) ||
      !hasAllowedKeys(chart.xAxis, ['label', 'values']) ||
      !Array.isArray(chart.xAxis.values) ||
      !isRecord(chart.yAxis) ||
      !hasAllowedKeys(chart.yAxis, ['label', 'unit']) ||
      !isOptionalString(chart.xAxis.label, 120) ||
      !isOptionalString(chart.yAxis.label, 120) ||
      !isOptionalString(chart.yAxis.unit, 120)
    ) {
      return null;
    }
    const categories = chart.xAxis.values;
    if (
      categories.length < (chart.type === 'line' ? 2 : 1) ||
      categories.length > 60 ||
      !categories.every(value => isRequiredString(value, 80))
    ) {
      return null;
    }
    if (!Array.isArray(chart.series) || chart.series.length < 1 || chart.series.length > 6)
      return null;
    if (!chart.series.every(series => isValidSeries(series, categories.length))) return null;

    return chart as AiChart;
  } catch {
    return null;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasOnlyKeys = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).length === keys.length && Object.keys(value).every(key => keys.includes(key));

const hasAllowedKeys = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).every(key => keys.includes(key));

const isRequiredString = (value: unknown, maxLength: number): value is string =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= maxLength &&
  value.trim() === value;

const isOptionalString = (value: unknown, maxLength: number): value is string | undefined =>
  value === undefined || isRequiredString(value, maxLength);

const isValidSeries = (value: unknown, categoryCount: number) => {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['name', 'values']) ||
    !isRequiredString(value.name, 120) ||
    !Array.isArray(value.values) ||
    value.values.length !== categoryCount
  ) {
    return false;
  }
  const values = value.values;
  return (
    values.some(item => typeof item === 'number') &&
    values.every(
      item =>
        item === null ||
        (typeof item === 'number' && Number.isFinite(item) && Math.abs(item) <= 1e15)
    )
  );
};

const getYAxisName = ({label, unit}: AiChart['yAxis']) => {
  if (label && unit) return `${label} (${unit})`;
  return label ?? unit;
};

export const AiChartBlock = ({source}: AiChartBlockProps) => {
  const chart = parseAiChart(source);
  const titleId = useId();
  const theme = useTheme();
  const {t} = useTranslation();

  if (!chart) return null;

  const option = {
    tooltip: {trigger: 'axis', renderMode: 'richText' as const},
    grid: {
      left: 48,
      right: 24,
      top: 24,
      bottom: 40,
      outerBoundsMode: 'same' as const,
      outerBoundsContain: 'all' as const
    },
    xAxis: {type: 'category' as const, data: chart.xAxis.values, name: chart.xAxis.label},
    yAxis: {type: 'value' as const, name: getYAxisName(chart.yAxis)},
    series: chart.series.map(series => ({name: series.name, type: chart.type, data: series.values}))
  };

  return (
    <Box component="figure" aria-labelledby={titleId} sx={{m: 0, my: 2}}>
      <Typography id={titleId} component="h3" variant="h6">
        {chart.title}
      </Typography>
      <ReactECharts
        theme={theme.palette.mode}
        option={option}
        style={{height: 320, width: '100%'}}
      />
      <Table size="small" aria-label={t('AIチャートのデータ表', {title: chart.title})}>
        <TableHead>
          <TableRow>
            <TableCell>系列</TableCell>
            {chart.xAxis.values.map((value, index) => (
              <TableCell key={`${value}-${index}`}>{value}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {chart.series.map(series => (
            <TableRow key={series.name}>
              <TableCell>{series.name}</TableCell>
              {series.values.map((value, index) => (
                <TableCell key={`${chart.xAxis.values[index]}-${index}`}>{value ?? '—'}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default AiChartBlock;
